import { useCallback, useMemo } from 'react';
import _get from 'lodash/get';
import {
  FilterGroupingEnum,
  Task,
  TaskFilterInputObject,
  TaskScopeInputObject,
  TaskScopeNameEnum,
  TasksMultiselectListQueryVariables,
  useDashboardTaskIdsLazyQuery,
  useTasksMultiselectListQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryResultWithIds } from '../types';

type UseTasksMultiselectQueryHookProps = Omit<ListQueryHookProps, 'ids'> & {
  scope?: TaskScopeInputObject;
  includeCategoryData?: boolean;
  documentationsId?: string;
  // TaskFilterInputObject has no `id` filter; filter by group_key instead so
  // the Selected tab can fetch exactly the chosen recurring-task chains.
  groupKeys?: string[];
};

const DEFAULT_SEARCH_SCHEMA = [
  { fieldName: 'name' },
  { fieldName: 'formCategoryName' },
  { fieldName: 'siteName' },
  { fieldName: 'siteAreaName' },
  { fieldName: 'siteCode' },
  { fieldName: 'siteAreaCode' },
  { fieldName: 'assetName' },
  { fieldName: 'assetCode' },
  { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
];

export default function useTasksMultiselectQueryHook({
  additionalFilters,
  documentationsId,
  fetchPolicy = 'cache-and-network',
  groupKeys,
  orderingParams,
  paginationParams,
  scope,
  searchQuery,
  searchQueryFilterSchema,
  skip,
  usePrevious = true,
}: UseTasksMultiselectQueryHookProps): ListQueryResultWithIds<Task> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );

  const resolvedScope: TaskScopeInputObject = useMemo(
    () =>
      scope ?? {
        name: TaskScopeNameEnum.Active,
      },
    [scope],
  );

  const pageNumber = Number(page);
  const limitNumber = Number(perPage);
  const variables: TasksMultiselectListQueryVariables = {
    scope: resolvedScope,
    page: Number.isNaN(pageNumber) ? undefined : pageNumber,
    limit: Number.isNaN(limitNumber) ? undefined : limitNumber,
  };

  const filters: TaskFilterInputObject[] = [];

  if (searchQuery) {
    const schema =
      searchQueryFilterSchema && searchQueryFilterSchema.length
        ? // Inherit the predicate type from DEFAULT_SEARCH_SCHEMA so integer
          // fields (e.g. `priority`) keep their `eq` predicate instead of
          // falling back to `cont`, which the backend rejects for Int filters.
          searchQueryFilterSchema.map(
            (fieldName) =>
              DEFAULT_SEARCH_SCHEMA.find(
                (field) => field.fieldName === fieldName,
              ) ?? { fieldName },
          )
        : DEFAULT_SEARCH_SCHEMA;
    filters.push(...createSearchFilters(searchQuery, schema));
  }

  if (documentationsId) {
    filters.push({
      documentationsId: {
        grouping: FilterGroupingEnum.And,
        predicate: { eq: documentationsId },
        filterGroup: 'documentationsId',
      },
    });
  }

  if (groupKeys?.length) {
    filters.push({
      groupKey: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: groupKeys },
        filterGroup: 'groupKey',
      },
    });
  }

  if (additionalFilters?.length) {
    filters.push(...(additionalFilters as TaskFilterInputObject[]));
  }

  if (filters.length) {
    variables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    variables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { data, previousData, loading, error, refetch } =
    useTasksMultiselectListQuery({
      fetchPolicy,
      variables,
      skip,
    });

  const [fetchIdsQuery] = useDashboardTaskIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          onlyIds: true,
          filters: variables.filters,
          scope: resolvedScope,
        },
      }),
    [fetchIdsQuery, variables.filters, resolvedScope],
  );

  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'tasks.collection', []) as Task[]);
  const metadata = _get(queryData, 'tasks.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
